/**
 * 分镜视频 API Hook
 * 对接 /api/v1/storyboard-resources 分镜视频接口
 */
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { storyboardResourcesApi } from '@/services/api/storyboard-resources';
import type { StoryboardVideoResponse } from '@/services/api/storyboard-resources';
import {
  getStoryboardBoardListsData,
  invalidateStoryboardBoardContentQueries,
  invalidateStoryboardBoardQueries,
  setStoryboardBoardDataByProject,
} from '@/hooks/api/useStoryboardBoard';
import { invalidateMentionQueriesForStoryboard } from '@/hooks/api/useMentionableResources';
import {
  setStoryboardVideoActiveInList,
  sortStoryboardVideoPreviewItems,
} from '@/lib/storyboard-preview-item';
import {
  reorderStoryboardBoardVideoItemsForStoryboard,
  syncStoryboardBoardVideoActiveForStoryboard,
} from '@/lib/storyboard-video-order';

export const storyboardVideosKeys = {
  all: ['storyboard-videos'] as const,
  listPrefix: (storyboardId: string) => [...storyboardVideosKeys.all, storyboardId] as const,
  list: (storyboardId: string, planId?: string | null) =>
    [...storyboardVideosKeys.listPrefix(storyboardId), { planId: planId ?? null }] as const,
  trimStatus: (videoId: string) => [...storyboardVideosKeys.all, 'trim-status', videoId] as const,
};

function applyStoryboardVideoDisplayOrder(
  videos: readonly StoryboardVideoResponse[],
  orderedVideoIds: readonly string[],
  activeVideoIds?: readonly string[]
): StoryboardVideoResponse[] {
  const orderByVideoId = new Map(orderedVideoIds.map((videoId, index) => [videoId, index]));
  const activeVideoIdSet = activeVideoIds ? new Set(activeVideoIds) : null;
  return sortStoryboardVideoPreviewItems(
    videos.map((video) => {
      const nextOrder = orderByVideoId.get(video.videoId);
      const nextActive = activeVideoIdSet?.has(video.videoId);
      if (nextOrder === undefined && nextActive === undefined) {
        return video;
      }
      return {
        ...video,
        ...(nextOrder !== undefined ? { displayOrder: nextOrder } : {}),
        ...(nextActive !== undefined
          ? { isActive: nextActive, ...(nextActive ? { isFinalized: true } : {}) }
          : {}),
      };
    })
  );
}

export async function invalidateStoryboardVideoRelatedQueries(
  queryClient: QueryClient,
  storyboardId: string,
  projectId?: string | null
): Promise<void> {
  invalidateMentionQueriesForStoryboard(queryClient, storyboardId);
  await invalidateStoryboardBoardContentQueries(queryClient, projectId);
}

/**
 * 获取分镜的所有视频版本
 */
export function useStoryboardVideos(
  storyboardId: string | null,
  options?: {
    enabled?: boolean;
    planId?: string | null;
    /** 视频变更后同步/失效分镜看板缓存（与 useStoryboardBoardLogic 的 projectId 解析一致） */
    invalidateBoardProjectId?: string | null;
  }
) {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;
  const planId = options?.planId ?? null;
  const invalidateBoardProjectId = options?.invalidateBoardProjectId;
  const listQueryKey = storyboardVideosKeys.list(storyboardId ?? '', planId);

  const query = useQuery({
    queryKey: listQueryKey,
    queryFn: () => storyboardResourcesApi.getStoryboardVideos(storyboardId!, { planId }),
    enabled: !!storyboardId && enabled,
    staleTime: 1000 * 30,
  });

  const updateMutation = useMutation({
    mutationFn: (params: {
      videoId: string;
      name?: string;
      description?: string;
      isActive?: boolean;
      isFinalized?: boolean;
    }) =>
      storyboardResourcesApi.updateStoryboardVideo(params.videoId, {
        name: params.name,
        description: params.description,
        isActive: params.isActive,
        isFinalized: params.isFinalized,
      }),
    onMutate: async (params) => {
      if (!storyboardId) return { previousVideos: undefined, previousBoardLists: [] };
      const queryKey = listQueryKey;
      await queryClient.cancelQueries({ queryKey });
      const previousVideos = queryClient.getQueryData<StoryboardVideoResponse[]>(queryKey);
      const previousBoardLists =
        params.isActive !== undefined ? getStoryboardBoardListsData(queryClient) : [];
      let optimisticVideo: StoryboardVideoResponse | undefined;

      queryClient.setQueryData<StoryboardVideoResponse[]>(queryKey, (current = []) => {
        const patched = current.map((video) => {
          if (video.videoId !== params.videoId) {
            return video;
          }

          const nextVideo = {
            ...video,
            ...(params.name !== undefined ? { name: params.name } : {}),
            ...(params.description !== undefined ? { description: params.description } : {}),
            ...(params.isActive !== undefined ? { isActive: params.isActive } : {}),
            ...(params.isFinalized !== undefined ? { isFinalized: params.isFinalized } : {}),
            ...(params.isActive === true ? { isFinalized: true } : {}),
          };
          optimisticVideo = nextVideo;
          return nextVideo;
        });

        if (params.isActive !== undefined) {
          return setStoryboardVideoActiveInList(patched, params.videoId, params.isActive);
        }

        return sortStoryboardVideoPreviewItems(patched);
      });

      const optimisticBoardVideo = optimisticVideo;
      if (params.isActive !== undefined && invalidateBoardProjectId && optimisticBoardVideo) {
        setStoryboardBoardDataByProject(queryClient, invalidateBoardProjectId, (current) => {
          if (!current) return current;
          return syncStoryboardBoardVideoActiveForStoryboard({
            tracks: current,
            storyboardId,
            video: optimisticBoardVideo,
            isActive: params.isActive!,
          });
        });
      }

      return { previousVideos, previousBoardLists };
    },
    onError: (_error, _params, context) => {
      if (!storyboardId || !context?.previousVideos) return;
      queryClient.setQueryData(listQueryKey, context.previousVideos);
      context.previousBoardLists.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: (video, params) => {
      if (!storyboardId) return;
      queryClient.setQueryData<StoryboardVideoResponse[]>(listQueryKey, (current = []) => {
        const patched = current.map((item) => (item.videoId === video.videoId ? video : item));
        return params.isActive !== undefined
          ? setStoryboardVideoActiveInList(patched, video.videoId, video.isActive)
          : sortStoryboardVideoPreviewItems(patched);
      });

      if (params.isActive !== undefined && invalidateBoardProjectId) {
        setStoryboardBoardDataByProject(queryClient, invalidateBoardProjectId, (current) => {
          if (!current) return current;
          return syncStoryboardBoardVideoActiveForStoryboard({
            tracks: current,
            storyboardId,
            video,
            isActive: video.isActive,
          });
        });
      }
    },
    onSettled: async (_data, _error, params) => {
      if (!storyboardId) return;
      await queryClient.invalidateQueries({
        queryKey: storyboardVideosKeys.listPrefix(storyboardId),
      });
      if (params.isActive !== undefined) {
        await invalidateStoryboardVideoRelatedQueries(
          queryClient,
          storyboardId,
          invalidateBoardProjectId
        );
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (params: { videoId: string; scope?: 'single' | 'lineage' }) =>
      storyboardResourcesApi.deleteStoryboardVideo(params.videoId, { scope: params.scope }),
    onSuccess: async () => {
      if (storyboardId) {
        await queryClient.invalidateQueries({
          queryKey: storyboardVideosKeys.listPrefix(storyboardId!),
        });
      }
      await invalidateStoryboardBoardQueries(queryClient, invalidateBoardProjectId);
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (params: { orderedVideoIds: string[]; activeVideoIds?: string[] }) =>
      storyboardResourcesApi.reorderStoryboardVideos(
        storyboardId!,
        params.orderedVideoIds,
        params.activeVideoIds
      ),
    onMutate: async (params) => {
      if (!storyboardId) return { previousVideos: undefined, previousBoardLists: [] };
      const queryKey = listQueryKey;
      await queryClient.cancelQueries({ queryKey });
      const previousVideos = queryClient.getQueryData<StoryboardVideoResponse[]>(queryKey);
      const previousBoardLists = getStoryboardBoardListsData(queryClient);

      queryClient.setQueryData<StoryboardVideoResponse[]>(queryKey, (current = []) =>
        applyStoryboardVideoDisplayOrder(current, params.orderedVideoIds, params.activeVideoIds)
      );

      if (invalidateBoardProjectId) {
        setStoryboardBoardDataByProject(queryClient, invalidateBoardProjectId, (current) => {
          if (!current) return current;
          return reorderStoryboardBoardVideoItemsForStoryboard(
            current,
            storyboardId,
            params.orderedVideoIds
          );
        });
      }

      return { previousVideos, previousBoardLists };
    },
    onError: (_error, _params, context) => {
      if (!storyboardId) return;
      if (context?.previousVideos) {
        queryClient.setQueryData(listQueryKey, context.previousVideos);
      }
      context?.previousBoardLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: (videos, params) => {
      if (!storyboardId) return;
      queryClient.setQueryData(listQueryKey, videos);
      if (invalidateBoardProjectId) {
        setStoryboardBoardDataByProject(queryClient, invalidateBoardProjectId, (current) => {
          if (!current) return current;
          return reorderStoryboardBoardVideoItemsForStoryboard(
            current,
            storyboardId,
            params.orderedVideoIds
          );
        });
      }
    },
    onSettled: async () => {
      if (!storyboardId) return;
      await queryClient.invalidateQueries({
        queryKey: storyboardVideosKeys.listPrefix(storyboardId),
      });
      await invalidateStoryboardVideoRelatedQueries(
        queryClient,
        storyboardId,
        invalidateBoardProjectId
      );
    },
  });

  const trimMutation = useMutation({
    mutationFn: (params: { videoId: string; trimStart: number; trimEnd: number }) =>
      storyboardResourcesApi.trimStoryboardVideo(params.videoId, {
        trimStart: params.trimStart,
        trimEnd: params.trimEnd,
      }),
    onSuccess: (video) => {
      if (!storyboardId) return;
      const queryKey = listQueryKey;
      // 乐观写入列表即可；list invalidate 留给调用方最多一次 refetch，避免 /videos 连打。
      queryClient.setQueryData<StoryboardVideoResponse[]>(queryKey, (current = []) => {
        if (current.some((item) => item.videoId === video.videoId)) return current;
        return [video, ...current];
      });
      void invalidateStoryboardVideoRelatedQueries(
        queryClient,
        storyboardId,
        invalidateBoardProjectId
      );
    },
  });

  return {
    videos: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isStale: query.isStale,
    refetch: query.refetch,
    activateVideo: (videoId: string) => updateMutation.mutateAsync({ videoId, isActive: true }),
    updateVideo: updateMutation.mutateAsync,
    setVideoActive: (videoId: string, isActive: boolean) =>
      updateMutation.mutateAsync({ videoId, isActive }),
    setVideoFinalized: (videoId: string, isFinalized: boolean) =>
      updateMutation.mutateAsync({ videoId, isFinalized }),
    reorderVideos: (orderedVideoIds: string[], activeVideoIds?: string[]) =>
      reorderMutation.mutateAsync({ orderedVideoIds, activeVideoIds }),
    trimVideo: (videoId: string, trimStart: number, trimEnd: number) =>
      trimMutation.mutateAsync({ videoId, trimStart, trimEnd }),
    deleteVideo: (videoId: string, options?: { scope?: 'single' | 'lineage' }) =>
      deleteMutation.mutateAsync({ videoId, scope: options?.scope }),
    isActivating: updateMutation.isPending,
    isUpdating: updateMutation.isPending,
    isReordering: reorderMutation.isPending,
    isTrimming: trimMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

export function useStoryboardVideoTrimStatus(
  videoId: string | null,
  storyboardId: string | null,
  options?: { enabled?: boolean }
) {
  const queryClient = useQueryClient();
  const enabled = Boolean(videoId) && (options?.enabled ?? true);

  const query = useQuery({
    queryKey: videoId ? storyboardVideosKeys.trimStatus(videoId) : storyboardVideosKeys.all,
    queryFn: () => storyboardResourcesApi.getStoryboardVideoTrimStatus(videoId!),
    enabled,
    staleTime: 0,
    refetchInterval: (query) => {
      const video = query.state.data;
      if (!video) return 2000;
      return video.status === 1 || video.statusStr === 'processing' ? 2000 : false;
    },
  });

  useEffect(() => {
    const video = query.data;
    if (!video || !storyboardId) return;

    const listPrefixQueryKey = storyboardVideosKeys.listPrefix(storyboardId);
    queryClient.setQueriesData<StoryboardVideoResponse[]>(
      { queryKey: listPrefixQueryKey },
      (current = []) => {
        const exists = current.some((item) => item.videoId === video.videoId);
        if (!exists) return [video, ...current];
        return current.map((item) => (item.videoId === video.videoId ? video : item));
      }
    );

    if (video.status !== 1 && video.statusStr !== 'processing') {
      void queryClient.invalidateQueries({ queryKey: listPrefixQueryKey });
    }
  }, [query.data, queryClient, storyboardId]);

  return query;
}

export function invalidateStoryboardVideosList(
  queryClient: QueryClient,
  storyboardId: string
): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: storyboardVideosKeys.listPrefix(storyboardId) });
}

export function removeStoryboardVideosCache(queryClient: QueryClient, storyboardId: string): void {
  queryClient.removeQueries({ queryKey: storyboardVideosKeys.listPrefix(storyboardId) });
}
